"use client";

import IdeaValidator from "@/components/IdeaValidator";
import ProfileModal from "@/components/ProfileModal";
import { FadeIn } from "@/components/ui/motion";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Sparkles, User } from "lucide-react";
import Link from "next/link";
import { Suspense, useEffect, useState } from "react";

export default function AIValidatorPage() {
    const { user, logout } = useAuth();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        handleScroll(); // set initial state
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className="relative h-auto min-h-[calc(100vh-5rem)] flex flex-col justify-start pt-36 pb-12 md:pt-44 md:pb-20 w-full bg-white overflow-hidden">
            {/* Minimalist dot grid backdrop */}
            <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1.5px,transparent_1.5px)] [background-size:24px_24px] opacity-70 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
                {/* User Profile Widget */}
                {user && (
                    <div 
                        className={`fixed z-50 transition-all duration-300 ${scrolled ? "top-7" : "top-11"}`}
                        style={{
                            right: scrolled 
                                ? "max(16px, calc(50vw - 470px))" 
                                : "max(16px, calc(50vw - 510px))"
                        }}
                    >
                        <div className="relative group">
                            <button
                                aria-label="Toggle profile menu"
                                className="h-11 w-11 rounded-full bg-white/80 backdrop-blur-md border border-zinc-200/80 flex items-center justify-center hover:bg-gray-50 transition-colors overflow-hidden shadow-lg"
                            >
                                {user.photoURL ? (
                                    <img src={user.photoURL} alt="User" className="h-full w-full object-cover" />
                                ) : (
                                    <span className="font-bold text-gray-600">
                                        {user.displayName ? user.displayName[0].toUpperCase() : (user.email ? user.email[0].toUpperCase() : "U")}
                                    </span>
                                )}
                            </button>
                            {/* Dropdown */}
                            <div className="absolute right-0 top-full mt-2 w-48 bg-white/95 backdrop-blur-xl border border-zinc-200/80 shadow-xl rounded-2xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-right scale-95 group-hover:scale-100">
                                <div className="p-2">
                                    <Link
                                        href="/ai-validator/result"
                                        className="flex items-center px-4 py-2.5 text-sm font-medium hover:bg-gray-50 rounded-xl transition-colors text-gray-800"
                                    >
                                        <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
                                        My Analysis
                                    </Link>
                                    <button
                                        onClick={() => setIsProfileOpen(true)}
                                        className="w-full flex items-center px-4 py-2.5 text-sm font-medium hover:bg-gray-50 rounded-xl transition-colors text-left text-gray-800"
                                    >
                                        <User className="h-4 w-4 mr-2 text-blue-500" />
                                        Profile Settings
                                    </button>
                                    <button
                                        onClick={logout}
                                        className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                    >
                                        <LogOut className="h-4 w-4 mr-2" />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="w-full max-w-4xl mx-auto text-center space-y-8 mt-12 md:mt-0">
                    <FadeIn delay={0.1}>
                        <div className="space-y-4">
                            <h1 className="text-5xl md:text-7xl font-light tracking-tight mb-8 leading-[1.15] text-black">
                                Validate your <span className="font-semibold">Concept</span> <br />
                                in seconds<span className="text-primary">.</span>
                            </h1>
                            <p className="text-xl text-zinc-500 max-w-2xl mx-auto font-normal">
                                Use our AI-powered validator to get instant feedback, SWOT analysis, and a roadmap for your next big thing.
                            </p>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.3}>
                        <div className="flex flex-col items-center w-full">
                            <Suspense fallback={null}>
                                <IdeaValidator
                                    className="max-w-4xl w-full"
                                    mode="relative"
                                />
                            </Suspense>
                        </div>
                    </FadeIn>
                </div>
            </div>
            <ProfileModal isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} />
        </div>
    );
}
